/**
 * The geometry of the image viewer. The request of 5 October 2026 (third),
 * section 6.
 *
 * How far a photograph may be zoomed and dragged, where it sits when it is
 * shown whole, the transform that puts it on the tile it was opened from, and
 * what a finger that has let go meant. View geometry, not a rule of the lab, so
 * it sits with the other picture code and not in the domain. Pure, with its
 * test beside it: the viewer and its gesture hook do the measuring, and ask
 * these what the measurements mean.
 */

export interface Size {
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

/** A place on the screen. A `DOMRect` is one. */
export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * How a picture is being looked at: its zoom, and how far its centre has been
 * dragged from the centre of the stage it is shown on, in screen pixels.
 */
export interface ZoomView {
  zoom: number;
  x: number;
  y: number;
}

/** The whole picture, and as close as it goes. */
export const MIN_ZOOM = 1;
export const MAX_ZOOM = 4;

/** What a double tap zooms to. A second one returns to the whole picture. */
export const DOUBLE_TAP_ZOOM = 2.5;

/** The whole picture, in the middle of its stage. */
export const AT_REST: ZoomView = { zoom: MIN_ZOOM, x: 0, y: 0 };

/** A zoom held inside its range. Something that is not a number is the least zoom. */
export function clampZoom(value: number, min: number = MIN_ZOOM, max: number = MAX_ZOOM): number {
  if (Number.isNaN(value)) return min;
  return Math.min(max, Math.max(min, value));
}

/**
 * Zooms to `zoom` about `focal`, a point measured from the centre of the stage:
 * whatever part of the picture was under that point is still under it
 * afterwards. That is what makes a pinch open the picture between the two
 * fingers, and a double tap open it where it was tapped, and not about the
 * middle of the screen.
 */
export function zoomAbout(view: ZoomView, zoom: number, focal: Point): ZoomView {
  if (view.zoom <= 0) return { zoom, x: view.x, y: view.y };
  const ratio = zoom / view.zoom;
  return {
    zoom,
    x: focal.x - ratio * (focal.x - view.x),
    y: focal.y - ratio * (focal.y - view.y),
  };
}

/**
 * How far a picture of `content` size (at zoom 1) may be dragged either way on
 * a stage, at a zoom: until its edge meets the edge of the stage, and no
 * further. Nothing on an axis where the picture is no larger than the stage.
 */
export function panBounds(stage: Size, content: Size, zoom: number): Point {
  return {
    x: Math.max(0, (content.width * zoom - stage.width) / 2),
    y: Math.max(0, (content.height * zoom - stage.height) / 2),
  };
}

/** Never a negative zero, which a bound of nothing would otherwise leave behind. */
const plain = (value: number): number => (value === 0 ? 0 : value);

/** A drag held inside the bounds `panBounds` gave. */
export function clampPan(offset: Point, bounds: Point): Point {
  return {
    x: plain(Math.min(bounds.x, Math.max(-bounds.x, offset.x))),
    y: plain(Math.min(bounds.y, Math.max(-bounds.y, offset.y))),
  };
}

/**
 * Where a picture of `source` size sits inside `box` when it is shown whole:
 * as large as fits, nothing cropped or stretched, in the middle. What
 * `object-fit: contain` draws. A picture with no size yet takes the box.
 */
export function containRect(source: Size, box: Rect): Rect {
  if (source.width <= 0 || source.height <= 0) {
    return { left: box.left, top: box.top, width: box.width, height: box.height };
  }
  const scale = Math.min(box.width / source.width, box.height / source.height);
  const width = source.width * scale;
  const height = source.height * scale;
  return {
    left: box.left + (box.width - width) / 2,
    top: box.top + (box.height - height) / 2,
    width,
    height,
  };
}

/** The middle of a place on the screen. */
export function centreOf(rect: Rect): Point {
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}

/**
 * The transform that puts a picture drawn at `to` on the tile at `from`, for
 * the viewer to grow out of and shrink back into. `around` is the point the
 * transformed element scales about, the middle of `to` unless told otherwise.
 *
 * A tile is cropped to fill its frame and the viewer shows the whole picture,
 * so the picture is scaled to cover the tile, as it does there: the place and
 * the scale match, and the crop cannot, with a transform alone. Null when
 * either has no size, which is nothing to fly between.
 */
export function flyTransform(from: Rect, to: Rect, around: Point = centreOf(to)): string | null {
  if (from.width <= 0 || from.height <= 0 || to.width <= 0 || to.height <= 0) return null;
  const scale = Math.max(from.width / to.width, from.height / to.height);
  const tile = centreOf(from);
  const picture = centreOf(to);
  const dx = tile.x - around.x - scale * (picture.x - around.x);
  const dy = tile.y - around.y - scale * (picture.y - around.y);
  return `translate(${String(dx)}px, ${String(dy)}px) scale(${String(scale)})`;
}

/** Whether any of a place is on a screen of `view` size. A tile scrolled away is not. */
export function rectInView(rect: Rect, view: Size): boolean {
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.left < view.width &&
    rect.top < view.height &&
    rect.left + rect.width > 0 &&
    rect.top + rect.height > 0
  );
}

/** The transform that draws a view: dragged, then zoomed about its own centre. */
export function zoomTransform(view: ZoomView): string {
  return `translate(${String(view.x)}px, ${String(view.y)}px) scale(${String(view.zoom)})`;
}

/**
 * How far a finger travels before its drag means anything. Short of it, a
 * finger that only meant to tap moves nothing; past it, the way it went is the
 * way the drag goes until it is let go.
 */
export const SWIPE_SLOP_PX = 10;

export type SwipeAxis = 'x' | 'y';

/**
 * Which way a drag is going, once it has left the slop: across (between
 * pictures) or down the screen (to close). Null while it has not. The viewer
 * asks once and keeps the answer, so a drag across that wanders downwards
 * stays a drag across.
 */
export function swipeAxis(dx: number, dy: number, slop: number = SWIPE_SLOP_PX): SwipeAxis | null {
  if (Math.max(Math.abs(dx), Math.abs(dy)) <= slop) return null;
  return Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
}

/**
 * What a drag has to do to count. The viewer passes the bottom sheet's own
 * numbers, so a pull that closes a sheet closes a photograph too.
 */
export interface SwipeLimits {
  /** Short of this the finger has not dragged at all. */
  slop: number;
  /** A drag this long counts however slow it was. */
  distance: number;
  /** Or one this fast, in pixels a millisecond, */
  flickVelocity: number;
  /** provided it went at least this far. */
  flickMin: number;
}

export type SwipeVerdict = 'next' | 'previous' | 'close' | 'none';

/**
 * What a finger meant by a drag of `dx` across and `dy` down, let go at
 * `velocity` after `moves` moves.
 *
 * To the left is the next picture and to the right the one before, as a page
 * is turned; downwards closes; upwards is nothing. Far enough counts, and so
 * does a flick from a shorter drag, which is a run of moves and never a single
 * jump, and has to be going the way the drag went.
 */
export function swipeVerdict(
  dx: number,
  dy: number,
  velocity: Point,
  moves: number,
  limits: SwipeLimits,
): SwipeVerdict {
  const axis = swipeAxis(dx, dy, limits.slop);
  if (axis === null) return 'none';

  if (axis === 'x') {
    const far = Math.abs(dx);
    const speed = velocity.x * Math.sign(dx);
    const flicked = moves >= 2 && speed > limits.flickVelocity && far > limits.flickMin;
    if (far > limits.distance || flicked) return dx < 0 ? 'next' : 'previous';
    return 'none';
  }

  const flicked = moves >= 2 && velocity.y > limits.flickVelocity && dy > limits.flickMin;
  return dy > limits.distance || flicked ? 'close' : 'none';
}
